// src/components/CandidatesTab.jsx
import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import { useCandidates, CANDIDATE_STATUSES } from '../hooks/useCandidates.js'
import { TimeInput }         from './TimeInput.jsx'
import { DateInput }         from './DateInput.jsx'
import { MultiSelect, SingleSelect, FILTER_CTRL, FILTER_CTRL_DEFAULT, FILTER_CTRL_ACTIVE, fmtPhone } from './AgentsTab.jsx'
import { useViewPreference } from '../hooks/useViewPreference.js'
import { getCandidateLogs, CAND_EVENT_TYPES, logCandidateEvent, getCurrentAdmin } from '../lib/agentLog.js'
import { getCandidateCvLink } from '../db/core.js'
import { t, tLog, getLanguage } from '../lib/i18n.js'
import { can } from '../lib/permissions.js'
import { toDateStr } from '../lib/attendance.js'
import { Icon, IconText } from './Icon.jsx'
// The SAME section the Add-client and Add-property modals use to mint a one-time link — one
// component, three kinds. It is already in the eager bundle (IntakeTab imports this module),
// so reusing it costs nothing and, more to the point, means the vacancy link inherits the
// status list, the WhatsApp hand-off and the "recent links" list rather than growing a second
// half-built copy of them.
import { FillLinkSection } from './desk/ManualClientModal.jsx'
// Posted vacancy links, each with an id and blockable on its own (scheduler 0017).
import { VacancyLinksSection } from './VacancyLinksSection.jsx'
import { COUNTER, pill } from './desk/primitives.jsx'
// ⚠️ TODAY COMES FROM THE OFFICE CLOCK, NEVER THE DEVICE (owner 2026-09-02: "make sure
// system dont use pcs time it used georgia tbilisi local"). `todayStr()` formats the
// SERVER-corrected instant in the office timezone, so neither a PC set to another country nor a
// PC whose clock is simply wrong can decide what day it is here.
import { todayStr, nowHour, tzMinute, now as clockNow } from '../lib/clock.js'

const dateLocale = () => getLanguage() === 'ka' ? 'ka-GE' : 'en-GB'

// i18n-array date formatting — browser ka locale data is unreliable
function fmtCardDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  return `${t('days')[(d.getDay() + 6) % 7]}, ${d.getDate()} ${t('months')[d.getMonth()]}`
}

// Ticks every 30s so countdowns stay fresh
function useNowMinute() {
  const [now, setNow] = useState(clockNow)
  useEffect(() => {
    const id = setInterval(() => setNow(clockNow()), 30000)
    return () => clearInterval(id)
  }, [])
  return now
}


// ─── Candidate timeline log (manager-facing, on the card) ─────────────────────
// variant="modal": rendered inside CandidateModal's interview tab (flat rounded box)
// instead of the card-bottom strip with negative margins.
// ── B1301 — A CANDIDATE NOTE IS AN ENTRY, NOT A FIELD ────────────────────────────────────
// Owner 2026-09-13, pointing at the interview-notes box: *"make this candidate note like the
// [property] one — add next, add next, that shows time, who and what."*
//
// `candidates.notes` is ONE text column, so every save overwrote the last person's text and
// nothing recorded who wrote it or when. The thread below writes `candidate_logs` rows instead,
// which have carried `ts`, `by`, `by_agent_id` and `by_office_id` since they were introduced —
// so this needs NO new table and no DDL in the scheduler project (that project takes DDL by
// hand, which would have made a new table a blocker rather than a change).
//
// ⚠️ THE OLD COLUMN IS READ, NEVER WRITTEN, AND NEVER DELETED. Whatever is in `notes` today is
// shown as a labelled "earlier notes" block above the thread. Dropping it would destroy the
// interview notes this feature exists to keep, and rewriting it into log rows would invent an
// author and a timestamp for text that has neither.
//
// ⚠️ Only for a SAVED candidate. A new one has no id to hang a log row on, so the create form
// keeps the plain textarea — see its call site.
function CandidateNoteThread({ candidateId, legacyNotes }) {
  const [notes, setNotes] = useState(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const load = useCallback(() => {
    getCandidateLogs(candidateId).then(all => setNotes((all || []).filter(e => e.type === 'note')))
  }, [candidateId])
  useEffect(() => { load() }, [load])
  const add = async () => {
    const body = text.trim()
    if (!body || busy) return
    setBusy(true)
    // Optimism would be wrong here: the row carries a server-side author and clock, so a local
    // echo would show a time and a name that are guesses. Re-read instead.
    try { await logCandidateEvent(candidateId, 'note', body); setText(''); load() }
    finally { setBusy(false) }
  }
  const sorted = [...(notes || [])].reverse()
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground block mb-1">{t('cand_notes_thread_label')}</label>
      {legacyNotes && (
        <div className="mb-1.5 rounded border border-border/40 bg-muted/20 px-2 py-1.5">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground/50 mb-0.5">{t('cand_notes_legacy_label')}</div>
          <div className="text-xs whitespace-pre-wrap">{legacyNotes}</div>
        </div>
      )}
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {notes === null ? null
          : sorted.length === 0 && !legacyNotes
            ? <div className="text-xs text-muted-foreground/60">{t('cand_notes_none')}</div>
            : sorted.map(n => {
                const d = new Date(n.ts)
                return (
                  <div key={n.id} className="flex items-start gap-1.5">
                    <span className="text-sm shrink-0 mt-0.5">📝</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs whitespace-pre-wrap">{n.message}</div>
                      <div className="text-[11px] text-muted-foreground/50">
                        {n.by ? tLog('cand_note_by', { who: n.by }) : null}
                        {n.by ? ' · ' : ''}
                        {d.getDate()} {t('months')[d.getMonth()].slice(0, 3)} {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </div>
                    </div>
                  </div>
                )
              })}
      </div>
      <div className="flex gap-1 mt-1.5">
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          placeholder={t('cand_note_add_ph')}
          className="flex-1 rounded border border-input px-2 py-1 text-xs bg-background" />
        <button type="button" onClick={add} disabled={busy || !text.trim()}
          className="rounded border border-border px-2 py-1 text-xs hover:bg-accent/60 disabled:opacity-50">
          {t('cand_note_add_button')}
        </button>
      </div>
    </div>
  )
}

function CandidateTimeline({ candidateId, variant }) {
  const [expanded, setExpanded] = useState(false)
  const [logs,     setLogs]     = useState([])

  useEffect(() => {
    getCandidateLogs(candidateId).then(setLogs)
  }, [candidateId])

  if (!logs.length) return null

  // Show newest 3 unless expanded; newest on top
  const sorted  = [...logs].reverse()
  const visible = expanded ? sorted : sorted.slice(0, 3)

  return (
    <div className={variant === 'modal'
      ? 'mt-4 px-3 py-2.5 bg-muted/30 border border-border/40 rounded-xl'
      : 'mt-3 -mx-4 -mb-4 px-3 py-2.5 bg-muted/30 border-t border-border/30 rounded-b-xl'}>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/40 mb-1.5">{t('cand_history')}</div>
      <div className="space-y-1.5">
        {visible.map(entry => {
          const meta = CAND_EVENT_TYPES[entry.type] || CAND_EVENT_TYPES.note
          const d    = new Date(entry.ts)
          const dateStr = `${d.getDate()} ${t('months')[d.getMonth()].slice(0, 3)}`
          const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
          return (
            <div key={entry.id} className="flex items-start gap-2">
              <span className="text-sm shrink-0 mt-0.5">{meta.icon}</span>
              <div className="flex-1 min-w-0">
                <span className={`text-xs font-medium ${meta.color}`}>{entry.message}</span>
                {/* B1301 — WHO was recorded all along (`by`, written by logCandidateEvent) and
                    never rendered. The owner asked for "time, who and what"; two of the three
                    were already on screen and the third was one field away. */}
                {entry.by && <span className="text-xs text-muted-foreground/60 ml-1.5">· {entry.by}</span>}
                <span className="text-xs text-muted-foreground/40 ml-1.5">{dateStr} {timeStr}</span>
              </div>
            </div>
          )
        })}
      </div>
      {logs.length > 3 && (
        <button onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}
          className="mt-1.5 text-xs text-muted-foreground hover:text-foreground">
          {expanded ? '▲ Show less' : `▼ Show ${logs.length - 3} more`}
        </button>
      )}
    </div>
  )
}

// ─── 3-dot quick-action menu ──────────────────────────────────────────────────
// align='right': popup + contact submenu open toward the left — for menus sitting at the
// right edge of a full-width row (list view), where left-aligned popups overflow on mobile.
function QuickMenu({ candidate, onUpdate, onPromote, onDelete, onEdit, canDelete, align = 'left' }) {
  const [open,        setOpen]        = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [copied,      setCopied]      = useState(false)
  const ref = useRef(null)
  // True once a concrete contact action (copy/call/…) happened in this menu session,
  // so closing doesn't also log a redundant "opened" entry.
  const interacted = useRef(false)

  const phone = candidate.phone
  const rawPhone = phone ? phone.replace(/[\s\-().]/g, '').replace(/^(00)?995/, '+995') : null

  // Audit who reached out to this candidate and how (when captured by logCandidateEvent)
  function logContact(method) {
    logCandidateEvent(candidate.id, 'note',
      t('log_contact').replace('{method}', method).replace('{phone}', rawPhone || '—').replace('{by}', getCurrentAdmin()))
  }

  // Close the submenu; if nothing was clicked, log that it was merely opened
  function closeContact() {
    setContactOpen(o => {
      if (o && !interacted.current) logContact(t('ct_opened'))
      return false
    })
  }
  function toggleContact() {
    if (contactOpen) closeContact()
    else { interacted.current = false; setContactOpen(true) }
  }
  function doContact(method) { interacted.current = true; logContact(method) }

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); closeContact() } }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  async function copyNumber() {
    try { await navigator.clipboard.writeText(rawPhone) } catch {}
    setCopied(true)
    doContact(t('ct_copied'))
    setTimeout(() => setCopied(false), 1500)
  }

  const actions = []
  const hasSchedule = Boolean(candidate.interview_date)
  actions.push({
    label: hasSchedule ? t('int_reschedule') : t('int_schedule'),
    onClick: () => { onEdit(candidate, 'interview'); setOpen(false) }
  })
  // No-show: only from 'scheduled' (a missed interview). Deliberately NO undo — a no-show
  // is a historical fact; the way forward is int_reschedule (picking a new date flips
  // status back to 'scheduled' via handleInterviewDate). User decision 2026-07-11.
  if (candidate.status === 'scheduled') {
    actions.push({ label: t('cand_no_show_btn'), onClick: () => {
      if (confirm(t('cand_no_show_confirm').replace('{name}', candidate.name))) { onUpdate(candidate.id, { status: 'no_show' }); setOpen(false) }
    }, danger: true })
  }
  if (candidate.status === 'interviewed') {
    // Undo only within the 60s window after marking (interviewed_ts_* — see useCandidates)
    let undoOpen = false
    try {
      const ts = Number(localStorage.getItem(`interviewed_ts_${candidate.id}`) || 0)
      undoOpen = ts > 0 && Date.now() - ts < 60000
    } catch {}
    if (undoOpen) {
      actions.push({ label: t('cand_undo_interviewed'), onClick: () => {
        try { localStorage.removeItem(`interviewed_ts_${candidate.id}`) } catch {}
        onUpdate(candidate.id, { status: 'new' }); setOpen(false)
      } })
    }
  } else {
    actions.push({ label: t('int_mark'), onClick: () => {
      if (confirm(t('cand_interviewed_confirm').replace('{name}', candidate.name))) { onUpdate(candidate.id, { status: 'interviewed' }); setOpen(false) }
    }})
  }
  if (candidate.status !== 'hired') {
    actions.push({ label: t('int_hire'), onClick: async () => { await onPromote(candidate.id); setOpen(false) } })
  }
  if (candidate.status === 'rejected') {
    actions.push({ label: t('cand_undo_reject'), onClick: () => { onUpdate(candidate.id, { status: 'new' }); setOpen(false) } })
  } else {
    actions.push({ label: t('int_reject'), onClick: () => {
      if (confirm(t('cand_reject_confirm').replace('{name}', candidate.name))) { onUpdate(candidate.id, { status: 'rejected' }); setOpen(false) }
    }, danger: true })
  }
  actions.push({ divider: true })
  actions.push({ label: t('int_edit'), onClick: () => { onEdit(candidate); setOpen(false) } })
  // Delete is admin-only (candidates.delete cap) — UI gate, same caveat as permissions.js header
  if (canDelete) {
    actions.push({ label: t('int_delete'), onClick: () => { if (confirm(t('cand_delete_confirm').replace('{name}', candidate.name))) { onDelete(candidate.id) } setOpen(false) }, danger: true })
  }

  return (
    <div ref={ref} className="relative shrink-0" onClick={e => e.stopPropagation()}>
      <button onClick={() => { setOpen(o => !o); closeContact() }}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-lg leading-none"
        title={t('cand_quick_actions')}>⋮</button>
      {open && (
        <div className={`absolute top-8 z-40 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[185px] ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {rawPhone && (
            <>
              <div className="relative">
                <button onClick={toggleContact}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between">
                  <Icon name="phone" size={13} /> {t('cand_contact')} <span className="text-muted-foreground text-xs">›</span>
                </button>
                {contactOpen && (
                  <div className={`absolute top-0 z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[180px] ${align === 'right' ? 'right-full mr-1' : 'left-full ml-1'}`}>
                    <button onClick={copyNumber} title={t('ct_copied')}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between gap-2">
                      <span className="tabular-nums font-medium">{rawPhone}</span>
                      <span className={`text-xs shrink-0 ${copied ? 'text-emerald-500' : 'text-muted-foreground'}`}><Icon name={copied ? 'check' : 'clipboard'} size={12} /></span>
                    </button>
                    <div className="border-t border-border/50 my-1" />
                    <a href={`tel:${rawPhone}`} onClick={() => doContact('Call')} className="block px-3 py-2 text-sm hover:bg-accent transition-colors"><Icon name="phone" size={13} /> Call</a>
                    <a href={`https://wa.me/${rawPhone.replace('+', '')}`} target="_blank" rel="noopener noreferrer"
                      onClick={() => doContact('WhatsApp')}
                      className="block px-3 py-2 text-sm hover:bg-accent transition-colors"><Icon name="message-circle" /> WhatsApp</a>
                    <a href={`sms:${rawPhone}`} onClick={() => doContact('SMS')} className="block px-3 py-2 text-sm hover:bg-accent transition-colors"><Icon name="mail" size={13} /> SMS</a>
                  </div>
                )}
              </div>
              <div className="border-t border-border/50 my-1" />
            </>
          )}
          {actions.map((a, i) =>
            a.divider
              ? <div key={i} className="border-t border-border/50 my-1" />
              : <button key={i} onClick={a.onClick}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${a.danger ? 'text-destructive' : ''}`}>
                  {a.label}
                </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Candidate card ───────────────────────────────────────────────────────────
function CandidateCard({ candidate, onEdit, onUpdate, onPromote, onDelete, canDelete, flash }) {
  const meta = CANDIDATE_STATUSES[candidate.status] || CANDIDATE_STATUSES.new
  const now  = useNowMinute()
  const [notesExpanded, setNotesExpanded] = useState(false)

  return (
    <div ref={node => { if (flash && node) node.scrollIntoView({ block: 'nearest' }) }}
      className={`bg-card border rounded-xl p-4 hover:border-primary/30 hover:shadow-sm transition-all group flex flex-col ${flash ? 'border-primary ring-2 ring-primary/40 animate-pulse' : 'border-border'}`}>
      {/* Top row */}
      <div className="flex items-start gap-2 mb-2">
        <QuickMenu candidate={candidate} onUpdate={onUpdate} onPromote={onPromote} onDelete={onDelete} onEdit={onEdit} canDelete={canDelete} />
        <button onClick={() => onEdit(candidate)} className="flex-1 text-left min-w-0">
          <div className="font-semibold text-sm group-hover:text-primary transition-colors truncate">{candidate.name}</div>
          {/* Position · age — one muted line. The age is derived from birth_date at render
              (ageFromBirthDate), never stored, so a card cannot go stale between visits. */}
          {(candidate.position || ageFromBirthDate(candidate.birth_date) !== null) && (
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {[candidate.position, ageFromBirthDate(candidate.birth_date) !== null
                ? t('cand_age_years').replace('{n}', ageFromBirthDate(candidate.birth_date)) : null]
                .filter(Boolean).join(' · ')}
            </div>
          )}
        </button>
        <span className={`${COUNTER} gap-1 font-medium shrink-0 ${meta.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} inline-block`} />
          {meta.label}
        </span>
      </div>

      {/* Interview block — show for all statuses that have a date */}
      {candidate.interview_date && (() => {
        const today = toDateStr(now)
        const nowMins = now.getHours() * 60 + now.getMinutes()
        const ivMins = candidate.interview_time
          ? Number(candidate.interview_time.slice(0,2)) * 60 + Number(candidate.interview_time.slice(3,5))
          : null
        const isToday = candidate.interview_date === today
        // Missed: past date, OR today with time already passed — still stuck in 'scheduled'
        const timePassed = isToday && ivMins != null && nowMins > ivMins + 30
        const needsAction = candidate.status === 'scheduled' && (candidate.interview_date < today || timePassed)
        const isNoShow = candidate.status === 'no_show'
        const isUpcoming = candidate.interview_date >= today && !timePassed
        // Countdown for today's upcoming interview
        let countdown = null
        if (isToday && ivMins != null && candidate.status === 'scheduled' && !timePassed) {
          const diff = ivMins - nowMins
          if (diff <= 0)        countdown = 'now'
          else if (diff < 60)   countdown = `in ${diff}m`
          else                  countdown = `in ${Math.floor(diff/60)}h ${diff % 60}m`
        }
        return (
          <div className={`text-xs rounded-lg px-2.5 py-2 mb-2 flex items-center gap-2 ${
            needsAction
              ? 'bg-red-50 dark:bg-red-900/10 border border-red-400/70 dark:border-red-600/60'
              : isNoShow
              ? 'bg-orange-50 dark:bg-orange-900/10 border border-orange-400/60 dark:border-orange-600/50'
              : countdown
                ? 'bg-violet-50 dark:bg-violet-900/15 border border-violet-400/60 dark:border-violet-600/50'
                : isUpcoming
                  ? 'bg-violet-50 dark:bg-violet-900/10 border border-violet-200/60 dark:border-violet-800/40'
                  : 'bg-muted/40 border border-border/40'
          }`}>
            <span className={needsAction ? 'animate-pulse' : ''}>{needsAction ? '🔴' : isNoShow ? '🚫' : '📅'}</span>
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${
                needsAction ? 'text-red-600 dark:text-red-400'
                : isNoShow ? 'text-orange-600 dark:text-orange-400'
                : isUpcoming ? 'text-violet-700 dark:text-violet-400'
                : 'text-muted-foreground'
              }`}>
                <span className="opacity-60 mr-1">{t('cand_iv_label')}:</span>
                {fmtCardDate(candidate.interview_date)}
                {candidate.interview_time && <span className="ml-1 font-bold">{candidate.interview_time.slice(0,5)}</span>}
                {needsAction && <span className="ml-1.5 text-xs font-bold">— {t('cand_finalize_action')}</span>}
                {isNoShow && <span className="ml-1.5 text-xs font-bold">— {t('cand_no_show')}</span>}
              </div>
              {candidate.interviewer && <div className="text-muted-foreground">{candidate.interviewer}</div>}
            </div>
            {countdown && (
              <span className={`${COUNTER} shrink-0 font-semibold bg-violet-500 text-white animate-pulse`}>
                {countdown === 'now' ? <><Icon name="bell" size={12} /> {t('cand_iv_now')}</> : countdown}
              </span>
            )}
          </div>
        )
      })()}

      {/* Start date block — for 'starting' status */}
      {candidate.status === 'starting' && candidate.start_date && (() => {
        const today = toDateStr(now)
        const isStartToday = candidate.start_date === today
        const noShow = candidate.start_date < today
        return (
          <div className={`text-xs rounded-lg px-2.5 py-2 mb-2 flex items-center gap-2 ${
            noShow
              ? 'bg-red-50 dark:bg-red-900/10 border border-red-400/70 dark:border-red-600/60'
              : 'bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-300/60 dark:border-cyan-700/50'
          }`}>
            <span className={noShow || isStartToday ? 'animate-pulse' : ''}>{noShow ? '🔴' : '🚀'}</span>
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${noShow ? 'text-red-600 dark:text-red-400' : 'text-cyan-700 dark:text-cyan-400'}`}>
                <span className="opacity-60 mr-1">{t('cand_status_starting')}:</span>
                {fmtCardDate(candidate.start_date)}
                {candidate.start_time && <span className="ml-1 font-bold">{candidate.start_time.slice(0,5)}</span>}
                {noShow && <span className="ml-1.5 text-xs font-bold">— {t('cand_no_show')}? {t('cand_finalize_action')}</span>}
              </div>
            </div>
            {isStartToday && !noShow && (
              <span className={`${COUNTER} shrink-0 font-bold bg-cyan-500 text-white animate-pulse`}>
                {t('cand_starts_today')}
              </span>
            )}
          </div>
        )
      })()}

      {/* Contact */}
      <div className="space-y-0.5 text-xs text-muted-foreground">
        {candidate.phone && <div><Icon name="smartphone" /> {fmtPhone(candidate.phone)}</div>}
        {candidate.email && <div><Icon name="mail" /> {candidate.email}</div>}
        {candidate.contact_link && (
          <a href={candidate.contact_link} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex items-center gap-1 text-primary hover:underline truncate">
            <Icon name="external-link" size={12} /> {(() => { try { return new URL(candidate.contact_link).hostname.replace('www.','') } catch { return candidate.contact_link.slice(0,28) } })()}
          </a>
        )}
      </div>

      {/* Source */}
      {candidate.source && (
        <div className="mt-2">
          <span className="text-xs px-1.5 py-0.5 bg-muted/60 rounded text-muted-foreground">{candidate.source}</span>
        </div>
      )}

      {/* Notes excerpt — click to expand */}
      {candidate.notes && (
        <button onClick={e => { e.stopPropagation(); setNotesExpanded(v => !v) }}
          className="mt-2 text-left text-xs text-muted-foreground bg-muted/30 rounded-lg px-2 py-1.5 hover:bg-muted/50 transition-colors w-full">
          <span className="mr-1"><Icon name="message-circle" /></span>
          {notesExpanded || candidate.notes.length <= 80
            ? candidate.notes
            : candidate.notes.slice(0, 80) + '…'}
        </button>
      )}

      {/* Hired / rejected banners */}
      {candidate.status === 'hired' && (
        <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-2 py-1.5 font-medium">
          <Icon name="check" size={13} /> {t('cand_hired_msg')}
        </div>
      )}

      {/* Timeline log */}
      <div className="flex-1" />
      <CandidateTimeline candidateId={candidate.id} />
    </div>
  )
}

// ─── Upcoming interviews sub-tab ──────────────────────────────────────────────
function InterviewsView({ candidates, onEdit, flashId, onUpdate, onPromote, onDelete, canDelete }) {
  const today = todayStr()

  // 'starting' has its OWN section (owner 2026-09-28) keyed on start_date, so it is kept out of
  // the interview-date lists below — its interview is usually past, which buried an agent who
  // starts tomorrow under წარსული (or under დასაგეგმია when it had no interview date at all).
  const all = useMemo(() => candidates
    .filter(c => c.interview_date && c.status !== 'rejected' && c.status !== 'starting')
    .sort((a, b) => {
      const da = a.interview_date + (a.interview_time || '00:00')
      const db = b.interview_date + (b.interview_time || '00:00')
      return da.localeCompare(db)
    }), [candidates])

  const todayList  = all.filter(c => c.interview_date === today)
  const futureList = all.filter(c => c.interview_date > today)
  // Past reads newest-first: last week's interview matters more than last month's
  const pastList   = all.filter(c => c.interview_date < today).reverse()
  // Candidates with no interview date were invisible in this (default) view — a freshly
  // added candidate "disappeared" right after saving. Surface them as an action queue.
  const unscheduled = useMemo(() => candidates
    .filter(c => !c.interview_date && c.status !== 'rejected' && c.status !== 'starting')
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')), [candidates])
  // Soonest start first; a start with no date (not settable from the modal, but old rows) last.
  const startingList = useMemo(() => candidates
    .filter(c => c.status === 'starting')
    .sort((a, b) => (a.start_date || '9999').localeCompare(b.start_date || '9999')
      || (a.start_time || '').localeCompare(b.start_time || '')), [candidates])

  function Row({ c, isPast, starting }) {
    const date   = starting ? c.start_date : c.interview_date
    const time   = starting ? c.start_time : c.interview_time
    const d      = new Date(date + 'T12:00:00')
    const isToday = date === today
    const meta   = CANDIDATE_STATUSES[c.status] || CANDIDATE_STATUSES.new
    const isFlash = c.id === flashId
    // A past row is dimmed by its CONTENT, never by the row container: `opacity`
    // applies to every descendant with no way for a child to opt out, so dimming
    // the container also dimmed the ⋮ menu to 50% — and, because opacity < 1 opens
    // a stacking context, trapped the open menu inside its own row so the rows
    // below painted over it. Leave the container opaque and dim the columns.
    const dim = isPast ? 'opacity-50' : ''
    return (
      <div onClick={() => onEdit(c, 'interview')} role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter') onEdit(c, 'interview') }}
        ref={node => { if (isFlash && node) node.scrollIntoView({ block: 'nearest' }) }}
        className={`w-full flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-3 text-left cursor-pointer hover:bg-accent/50 transition-colors border-b border-border/50 last:border-0 first:rounded-t-xl last:rounded-b-xl ${isFlash ? 'bg-primary/10 animate-pulse' : ''}`}>
        <div className={`text-center shrink-0 w-11 ${dim} ${isToday ? 'text-primary' : ''}`}>
          {date ? (<><div className="text-xs font-semibold uppercase text-muted-foreground">{d.toLocaleDateString(dateLocale(), { weekday: 'short' })}</div>
          <div className={`text-2xl font-bold leading-tight ${isToday ? 'text-primary' : ''}`}>{d.getDate()}</div>
          <div className="text-xs text-muted-foreground">{d.toLocaleDateString(dateLocale(), { month: 'short' })}</div>
          </>) : <span className="text-lg text-muted-foreground">—</span>}
        </div>
        {/* mobile: hours over minutes (narrow column); desktop: normal 13:30 */}
        <div className={`font-bold tabular-nums shrink-0 text-center w-8 sm:w-14 ${dim} ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
          {time ? (
            <>
              <div className="sm:hidden text-base leading-[1.45]">
                <div>{time.slice(0, 2)}</div>
                <div className="text-muted-foreground/70">{time.slice(3, 5)}</div>
              </div>
              <span className="hidden sm:inline text-lg">{time.slice(0, 5)}</span>
            </>
          ) : <span className="text-lg">—</span>}
        </div>
        {/* min-w-0 + truncate: long Georgian names must not wrap under / collide with the badges (mobile) */}
        <div className={`flex-1 min-w-0 ${dim}`}>
          <div className="font-semibold text-sm truncate">{c.name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {c.position && <span>{c.position}</span>}
            {c.interviewer && <span className="ml-2">· {c.interviewer}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* TODAY chip is redundant inside the "Today" section header — hide on mobile to save width */}
          {isToday && <span className={`${COUNTER} hidden sm:inline font-bold text-white bg-primary`}>{t('cand_today_badge')}</span>}
          {/* every row here IS an interview — drop the redundant "გასაუბრება" prefix (ka labels only) */}
          <span className={`${COUNTER} font-medium whitespace-nowrap ${dim} ${meta.color}`}>{meta.label.replace(/^გასაუბრება\s+/, '')}</span>
          <QuickMenu candidate={c} align="right" canDelete={canDelete}
            onUpdate={onUpdate} onPromote={onPromote} onDelete={onDelete} onEdit={onEdit} />
        </div>
      </div>
    )
  }

  if (!all.length && !unscheduled.length && !startingList.length) return (
    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground text-sm gap-2">
      <span className="text-4xl"><Icon name="calendar" size={36} /></span>
      <p>{t('int_no_scheduled')}</p>
      <p className="text-xs">{t('int_no_scheduled_hint')}</p>
    </div>
  )

  return (
    <div className="space-y-4">
      {todayList.length > 0 && (
        <div className="bg-card border border-primary/30 rounded-xl">
          <div className="px-4 py-2.5 bg-primary/5 border-b border-border flex items-center gap-2 rounded-t-xl">
            <span className="text-sm font-semibold text-primary">{t('int_today')}</span>
            <span className="text-xs text-muted-foreground">{todayList.length} {t('int_count')}</span>
          </div>
          {todayList.map(c => <Row key={c.id} c={c} />)}
        </div>
      )}
      {startingList.length > 0 && (
        <div className="bg-card border border-cyan-400/40 rounded-xl">
          <div className="px-4 py-2.5 bg-cyan-500/5 border-b border-border flex items-center gap-2 rounded-t-xl">
            <span className="text-sm font-semibold text-cyan-600 dark:text-cyan-400">{t('int_starting')}</span>
            <span className="text-xs text-muted-foreground">{startingList.length}</span>
          </div>
          {startingList.map(c => <Row key={c.id} c={c} starting />)}
        </div>
      )}
      {futureList.length > 0 && (
        <div className="bg-card border border-border rounded-xl">
          <div className="px-4 py-2.5 border-b border-border">
            <span className="text-sm font-semibold">{t('int_upcoming')}</span>
          </div>
          {futureList.map(c => <Row key={c.id} c={c} />)}
        </div>
      )}
      {unscheduled.length > 0 && (
        <div className="bg-card border border-amber-400/40 rounded-xl">
          <div className="px-4 py-2.5 bg-amber-500/5 border-b border-border flex items-center gap-2 rounded-t-xl">
            <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">{t('int_unscheduled')}</span>
            <span className="text-xs text-muted-foreground">{unscheduled.length} · {t('int_unscheduled_hint')}</span>
          </div>
          {unscheduled.map(c => {
            const meta = CANDIDATE_STATUSES[c.status] || CANDIDATE_STATUSES.new
            const isFlash = c.id === flashId
            return (
              <div key={c.id} onClick={() => onEdit(c, 'interview')} role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') onEdit(c, 'interview') }}
                ref={node => { if (isFlash && node) node.scrollIntoView({ block: 'nearest' }) }}
                className={`w-full flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-3 text-left cursor-pointer hover:bg-accent/50 transition-colors border-b border-border/50 last:border-0 last:rounded-b-xl ${isFlash ? 'bg-primary/10 animate-pulse' : ''}`}>
                <span className="shrink-0 w-11 text-center"><Icon name="clipboard" size={18} /></span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.position && <span>{c.position}</span>}
                    {c.created_at && <span className="ml-2">· {fmtCardDate(c.created_at.slice(0, 10))}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  <span className={`${COUNTER} font-medium whitespace-nowrap ${meta.color}`}>{meta.label}</span>
                  <QuickMenu candidate={c} align="right" canDelete={canDelete}
                    onUpdate={onUpdate} onPromote={onPromote} onDelete={onDelete} onEdit={onEdit} />
                </div>
              </div>
            )
          })}
        </div>
      )}
      {pastList.length > 0 && (
        <div className="bg-card border border-border rounded-xl">
          <div className="px-4 py-2.5 border-b border-border">
            <span className="text-sm font-semibold text-muted-foreground">{t('int_past')}</span>
          </div>
          {pastList.map(c => <Row key={c.id} c={c} isPast />)}
        </div>
      )}
    </div>
  )
}

// ─── Candidate form modal ─────────────────────────────────────────────────────
// canDelete: admin-only (candidates.delete cap). Defaults false so other entry points
// (IntakeTab, which passes a no-op onDelete anyway) never show the button.
// Whole years since the birth date, or null when there is no usable one. One definition:
// the modal shows it and the card meta line reads the same function, so they cannot disagree.
export function ageFromBirthDate(iso) {
  if (!/^d{4}-d{2}-d{2}$/.test(String(iso || ''))) return null
  const [y, m, d] = String(iso).split('-').map(Number)
  const now = new Date()
  let age = now.getFullYear() - y
  // Month is 1-based here and 0-based on Date — comparing the two is the classic way this
  // lands a year out for everyone with a birthday later in the year.
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1
  return age >= 0 && age < 120 ? age : null
}

// What a self-filled application answered, rendered as one muted line of chips under the
// contact fields. READ-ONLY on purpose: these are the applicant's own words about themselves,
// and a field a manager can quietly edit stops being that. Corrections belong in notes, where
// they carry a byline.
// ⚠️ The codes are the ones the DB's CHECK constraints allow (scheduler 0014) and the labels are
// resolved here, not stored — the applicant answered in one of three languages and the office
// reads in another.
const SELF_ANSWER_LABELS = {
  study_status: { no: 'cand_study_no', student: 'cand_study_student', pupil: 'cand_study_pupil' },
  sales_experience: { none: 'cand_sales_none', lt1: 'cand_sales_lt1', '1_3': 'cand_sales_1_3', '3p': 'cand_sales_3p' },
  available_from: { now: 'cand_avail_now', '1w': 'cand_avail_1w', '2w': 'cand_avail_2w', other: 'cand_avail_other' },
}

function SelfReportedRow({ candidate }) {
  const [cvBusy, setCvBusy] = useState(false)
  const [cvErr, setCvErr] = useState('')
  const chips = []
  for (const [field, map] of Object.entries(SELF_ANSWER_LABELS)) {
    const key = map[candidate?.[field]]
    if (key) chips.push(t(key))
  }
  if (candidate?.heard_from) chips.push(t('cand_heard_from').replace('{v}', candidate.heard_from))
  if (!chips.length && !candidate?.cv_url) return null

  // The CV is fetched as a SIGNED, ten-minute url at the moment of the click — the row stores a
  // path, never a public link, because the file carries a person's name, phone and history.
  async function openCv() {
    setCvBusy(true); setCvErr('')
    try {
      const d = await getCandidateCvLink(candidate.cv_url)
      if (!d?.url) throw new Error('no url')
      window.open(d.url, '_blank', 'noopener')
    } catch { setCvErr(t('cand_cv_fail')) }
    finally { setCvBusy(false) }
  }

  return (
    <div className="col-span-2 -mt-1 space-y-1.5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground/60">{t('cand_selfreported')}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map((c, n) => (
          <span key={n} className="rounded-lg bg-accent/50 px-2 py-1 text-xs text-muted-foreground">{c}</span>
        ))}
        {candidate?.cv_url && (
          <button type="button" onClick={openCv} disabled={cvBusy}
            className="rounded-lg border border-border px-2 py-1 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-60">
            <IconText>{`📄 ${cvBusy ? t('cand_cv_opening') : t('cand_cv_open')}`}</IconText>
          </button>
        )}
      </div>
      {cvErr && <p className="text-xs text-destructive">{cvErr}</p>}
    </div>
  )
}

export function CandidateModal({ candidate, initialTab, config = {}, onSave, onDelete, onPromote, onClose, canDelete = false, me = null, offices = [], seeAllVacancyLinks = false, canSetVacancyLinkPolicy = false }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const isEdit  = Boolean(candidate?.id)
  const isHired = candidate?.status === 'hired'

  const [name,          setName]          = useState(candidate?.name          || '')
  const [email,         setEmail]         = useState(candidate?.email         || '')
  const [phone,         setPhone]         = useState(candidate?.phone         || '')
  const [contactLink,   setContactLink]   = useState(candidate?.contact_link  || '')
  const [position,      setPosition]      = useState(candidate?.position      || '')
  const [source,        setSource]        = useState(candidate?.source        || '')
  const [district,      setDistrict]      = useState(candidate?.district      || '')
  // A DATE, never an age (scheduler migration 0013). An age is a fact with a decay date: a
  // card read three months later would state a number that has quietly become wrong. The age
  // beside the field is computed at render, from this.
  const [birthDate,     setBirthDate]     = useState(candidate?.birth_date    || '')
  // When to call them (scheduler 0024; asked by the sentavus.com job form, editable here). A Postgres `time` comes
  // back as HH:MM:SS, the input speaks HH:MM.
  const hm = v => (v ? String(v).slice(0, 5) : '')
  const [contactPref,   setContactPref]   = useState(candidate?.contact_preference || '')
  const [ctFrom,        setCtFrom]        = useState(hm(candidate?.contact_time_from))
  const [ctTo,          setCtTo]          = useState(hm(candidate?.contact_time_to))
  const [ctErr,         setCtErr]         = useState('')
  const [languages,     setLanguages]     = useState(() => Array.isArray(candidate?.languages) ? candidate.languages : [])
  const [status,        setStatus]        = useState(candidate?.status        || 'new')
  const [startDate,     setStartDate]     = useState(candidate?.start_date    || '')
  const [startTime,     setStartTime]     = useState(candidate?.start_time    || '')
  const [showStartPicker, setShowStartPicker] = useState(false)
  const [interviewDate, setInterviewDate] = useState(candidate?.interview_date || '')
  const [interviewTime, setInterviewTime] = useState(candidate?.interview_time || '')
  // NEW candidate → whoever is adding it conducts the interview by default (owner ask,
  // 2026-08-29). It is a default, not a rule — the field stays free text, so handing the
  // interview to someone else is still one edit. An EXISTING candidate keeps whatever is
  // stored, including a deliberately empty interviewer; overwriting that on open would
  // reassign interviews nobody asked to reassign.
  const [interviewer,   setInterviewer]   = useState(candidate?.interviewer || (candidate ? '' : (me?.name || '')))
  const [notes,         setNotes]         = useState(candidate?.notes         || '')
  const [saving,        setSaving]        = useState(false)
  const [promoting,     setPromoting]     = useState(false)
  const [nameErr,       setNameErr]       = useState('')
  // Single scrollable body (tabs removed 2026-07-29, user ask) — initialTab='interview'
  // now means "scroll to the interview section on open" (rows/quick-menu entry points).
  const interviewRef = useRef(null)
  useEffect(() => {
    if (initialTab === 'interview') interviewRef.current?.scrollIntoView({ block: 'start' })
  }, [])
  // "Seen" (owner 2026-09-19): opening an existing card records a `viewed` event — at most once
  // a day per viewer per candidate in this browser, so re-opening does not flood the timeline.
  useEffect(() => {
    if (!candidate?.id) return
    const key = `sv-cand-viewed-${candidate.id}`
    const today = new Date().toISOString().slice(0, 10)
    try { if (localStorage.getItem(key) === today) return; localStorage.setItem(key, today) } catch { /* private mode: log anyway */ }
    logCandidateEvent(candidate.id, 'viewed', 'opened').catch(() => {})
    try { window.dispatchEvent(new CustomEvent('sentavus:candidate-viewed', { detail: { id: candidate.id } })) } catch {}
  }, [candidate?.id])
  // 60s undo window after marking interviewed — footer countdown button, same pattern
  // as the 300s hire revert in AgentModal (interviewed_ts_* set in useCandidates.update).
  const [undoSecs,      setUndoSecs]      = useState(0)

  useEffect(() => {
    if (!isEdit || status !== 'interviewed') { setUndoSecs(0); return }
    let remaining = 0
    try {
      const ts = Number(localStorage.getItem(`interviewed_ts_${candidate.id}`) || 0)
      if (ts) remaining = 60 - Math.floor((Date.now() - ts) / 1000)
    } catch {}
    if (remaining <= 0) { setUndoSecs(0); return }
    setUndoSecs(remaining)
    const id = setInterval(() => {
      setUndoSecs(s => {
        if (s <= 1) {
          clearInterval(id)
          try { localStorage.removeItem(`interviewed_ts_${candidate.id}`) } catch {}
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [isEdit, status, candidate?.id])

  async function handleUndoInterviewed() {
    try { localStorage.removeItem(`interviewed_ts_${candidate.id}`) } catch {}
    setUndoSecs(0)
    await handleQuickStatus('new')
  }

  function handleInterviewDate(val) {
    setInterviewDate(val)
    if (val) setStatus('scheduled')
  }

  function toggleLanguage(lang) {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang])
  }

  function buildData(overrides = {}) {
    return {
      name: name.trim() || candidate?.name || '', email, phone, contact_link: contactLink || null,
      position, source, status,
      district: district || null,
      birth_date: birthDate || null,
      languages: languages.length > 0 ? languages : null,
      interview_date: interviewDate || null,
      interview_time: interviewTime || null,
      interviewer:    interviewer   || null,
      notes:          notes         || null,
      start_date:     startDate     || null,
      start_time:     startTime     || null,
      // sent only when changed, so a save never names columns the database may not have yet
      ...contactPatch(),
      ...overrides,
    }
  }

  function contactPatch() {
    const pref = contactPref || null
    const from = pref === 'custom' ? (ctFrom || null) : null
    const to = pref === 'custom' ? (ctTo || null) : null
    const same = pref === (candidate?.contact_preference || null)
      && from === (hm(candidate?.contact_time_from) || null) && to === (hm(candidate?.contact_time_to) || null)
    return same ? {} : { contact_preference: pref, contact_time_from: from, contact_time_to: to }
  }

  function contactError() {
    if (contactPref !== 'custom') return ''
    if (!/^\d\d:\d\d$/.test(ctFrom || '') || !/^\d\d:\d\d$/.test(ctTo || '') || ctFrom >= ctTo) return t('cand_ct_err')
    return ''
  }

  async function handleSave() {
    if (!name.trim()) { setNameErr(t('cand_name_err')); return }
    const ce = contactError()
    if (ce) { setCtErr(ce); return }
    setSaving(true)
    try {
      await onSave(buildData())
      onClose()
    } finally { setSaving(false) }
  }

  // New candidate: status buttons just set local state — persisted on "Add candidate"
  function toggleLocalStatus(target) {
    setStatus(s => s === target ? 'new' : target)
  }

  // Quick status change — saves immediately
  async function handleQuickStatus(newStatus) {
    if (!isEdit) { setStatus(newStatus); return }
    setSaving(true)
    try {
      await onSave({
        name: name.trim() || candidate.name, email, phone,
        contact_link: contactLink || null, position, source, status: newStatus,
        district: district || null, languages: languages.length > 0 ? languages : null,
        interview_date: interviewDate || null, interview_time: interviewTime || null,
        interviewer: interviewer || null, notes: notes || null,
        start_date: startDate || null, start_time: startTime || null,
      })
      if (newStatus === 'new' && (status === 'interviewed' || status === 'rejected')) {
        logCandidateEvent(candidate.id, 'note', `↩ Undid ${status}`)
      }
      setStatus(newStatus)
    } finally { setSaving(false) }
  }



  // Set/clear starting status with explicit start date/time (avoids stale state)
  async function handleStartingStatus(newStatus, sDate, sTime) {
    // New candidate: keep it local until "Add candidate" persists everything
    if (!isEdit) {
      setStatus(newStatus)
      setStartDate(sDate || '')
      setStartTime(sTime || '')
      return
    }
    setSaving(true)
    try {
      await onSave({
        name: name.trim() || candidate.name, email, phone,
        contact_link: contactLink || null, position, source, status: newStatus,
        district: district || null, languages: languages.length > 0 ? languages : null,
        interview_date: interviewDate || null, interview_time: interviewTime || null,
        interviewer: interviewer || null, notes: notes || null,
        start_date: sDate || null, start_time: sTime || null,
      })
      if (newStatus === 'interviewed') {
        logCandidateEvent(candidate.id, 'note', '↩ Undid starting')
      }
      setStatus(newStatus)
    } finally { setSaving(false) }
  }

  async function handlePromote() {
    // New candidate: create it first, then promote the freshly created record
    if (!isEdit) {
      if (!name.trim()) { setNameErr(t('cand_name_err')); return }
      setPromoting(true)
      try {
        const created = await onSave(buildData())
        if (created?.id) await onPromote(created.id)
      } finally { setPromoting(false) }
      return
    }
    setPromoting(true)
    try {
      await onPromote(candidate.id)
      // onPromote (handleHire) closes the modal and navigates — nothing else needed
    } finally { setPromoting(false) }
  }

  const inp = 'w-full rounded-lg border border-input px-3 py-2 text-base sm:text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
  const statusMeta = CANDIDATE_STATUSES[status]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-xl">

        {/* Header — name + status badge */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold">{isEdit ? candidate.name : t('cand_add_header')}</h2>
            <div className="flex items-center gap-2 mt-1">
              {isEdit && (
                <span className={`${COUNTER} font-semibold border ${statusMeta?.color || ''}`}>
                  {statusMeta?.label || status}
                </span>
              )}
              {isEdit && candidate.position && (
                <span className="text-xs text-muted-foreground">{candidate.position}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        {/* Hired banner */}
        {isHired && (
          <div className="mx-5 mt-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl px-4 py-2.5 text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
            {t('cand_hired_agents_msg')}
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">

          {/* THE PERSONAL LINK (owner 2026-09-09: "we need create link to send to candidate
              personally — that's the same shape as send fill for request and for adding
              property"). Shown only when ADDING: on an existing card the fields are already
              filled and re-sending a form to someone in the pipeline is a different act, not
              a first contact. The prefill is read LIVE from this form, so typing the person's
              name and number before pressing Generate carries them into their own form.
              ⚠️ Not to be confused with the PORTAL link, which is permanent, carries no token
              and is minted by nobody: /vacancy.html?src=<portal>. */}
          {!isEdit && (
            <FillLinkSection kind="candidate" agentId={me?.id || null} agentName={me?.name || ''}
              getPrefill={() => ({ name, phone })} />
          )}

          {/* POSTED LINKS (owner 2026-09-16, pointing at this modal: "here we need create link for
              which platform — choose — and comment on link to remember why it was created, so
              later they can block it"). A different object from the personal link above: that one
              is for ONE person and is spent by their submission; these are posted in public groups,
              stay open, and are blocked one by one. The creator becomes the recruiter on every
              application a link brings. Same condition as the section above — this is the door
              for bringing people IN, which an existing card has already passed through. */}
          {!isEdit && (
            <VacancyLinksSection me={me} adminName={getCurrentAdmin()}
              showAll={seeAllVacancyLinks} canSetPolicy={canSetVacancyLinkPolicy} />
          )}

          <div className="grid grid-cols-2 gap-3">
              {/* Byline — read-only, and read-only is the point: "who added this" is a fact,
                  not a field. Absent on rows created before 2026-08-29, which is why this is
                  conditional rather than a placeholder saying "unknown". */}
              {isEdit && candidate?.created_by_name && (
                <div className="col-span-2 -mb-1 text-xs text-muted-foreground/70">
                  {t('cand_added_by')} <span className="text-foreground/80 font-medium">{candidate.created_by_name}</span>
                  {candidate.created_at && <> · {new Date(candidate.created_at).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                  {/* The office this candidate will be hired INTO. Shown because it is decided
                      here and acted on much later, by someone else — an invisible destination is
                      how an agent ends up in a team nobody chose. Read-only: it is a fact about
                      who recruited them, and the office is still editable on the agent itself. */}
                  {candidate.office_id && (() => {
                    const o = offices.find(x => x.id === candidate.office_id)
                    return o ? <> · <span className="text-foreground/80">{o.name}</span></> : null
                  })()}
                </div>
              )}
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-1">{t('cand_fullname_label')}</label>
                <input value={name} onChange={e => { setName(e.target.value); setNameErr('') }}
                  {...{placeholder: t('cand_name_placeholder')}} className={`${inp} ${nameErr ? 'border-destructive' : ''}`} />
                {nameErr && <p className="text-xs text-destructive mt-1">{nameErr}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_phone_label') }</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} {...{placeholder: t('cand_phone_placeholder')}} className={inp} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_email_label') }</label>
                <input value={email} onChange={e => setEmail(e.target.value)} {...{placeholder: t('cand_email_placeholder')}} type="email" className={inp} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  {t('cand_link_label')}
                  <span className="ml-1.5 font-normal text-muted-foreground/60">{t('cand_link_hint')}</span>
                </label>
                <input value={contactLink} onChange={e => setContactLink(e.target.value)}
                  {...{placeholder: t('cand_link_placeholder')}} className={inp} />
                {contactLink && (
                  <a href={contactLink} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"><IconText>{t('cand_open_link')}</IconText></a>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_applying_label') }</label>
                <select value={position} onChange={e => setPosition(e.target.value)}
                  className="w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">{t('cand_select_role')}</option>
                  {(config.agent_roles?.length ? config.agent_roles : ['Agent','Senior Agent','Junior Agent','Manager','Admin']).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_source_label') }</label>
                <select value={source} onChange={e => setSource(e.target.value)}
                  className="w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">{t('cand_select_source')}</option>
                  {(config.candidate_sources?.length ? config.candidate_sources : ['Facebook','LinkedIn','Referral','Website','Other']).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_district_label') }</label>
                <select value={district} onChange={e => setDistrict(e.target.value)}
                  className="w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">{t('cand_select')}</option>
                  {(config.districts?.length ? config.districts : []).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_bday_label') }</label>
                <DateInput value={birthDate} onChange={setBirthDate} className={inp} />
                {(() => {
                  // Shown next to the date because the date is what gets typed and the age is
                  // what gets read — and seeing "8 years old" is how a mistyped year is caught
                  // at the moment it is typed rather than at the interview.
                  const y = ageFromBirthDate(birthDate)
                  return y === null ? null : <p className="mt-1 text-xs text-muted-foreground">{t('cand_age_years').replace('{n}', y)}</p>
                })()}
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_ct_label') }</label>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={contactPref} onChange={e => { setContactPref(e.target.value); setCtErr('') }}
                    className="rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="">—</option>
                    <option value="anytime">{t('cand_ct_anytime')}</option>
                    <option value="business_hours">{t('cand_ct_business')}</option>
                    <option value="custom">{t('cand_ct_custom')}</option>
                  </select>
                  {contactPref === 'custom' && (
                    <>
                      <TimeInput value={ctFrom} onChange={v => { setCtFrom(v); setCtErr('') }} className={inp + ' !w-28'} />
                      <span className="text-muted-foreground">–</span>
                      <TimeInput value={ctTo} onChange={v => { setCtTo(v); setCtErr('') }} className={inp + ' !w-28'} />
                    </>
                  )}
                </div>
                {ctErr && <p className="mt-1 text-xs text-destructive">{ctErr}</p>}
              </div>
              <SelfReportedRow candidate={candidate} />
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground block mb-2">{ t('cand_modal_languages_label') }</label>
                <div className="flex flex-wrap gap-2">
                  {(config.languages?.length ? config.languages : ['Georgian','English','Russian']).map(lang => (
                    <button key={lang} type="button" onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                        languages.includes(lang) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}>
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
          </div>

          {/* Interview section — below the info fields (single scrollable body) */}
          <div ref={interviewRef} className="space-y-4 pt-4 border-t border-border/60">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">{t('cand_modal_interview')}</div>

              {/* No-show is history, not a reversible state: show what was planned and how it
                  ended; the only way forward is picking a NEW date (→ 'scheduled'). */}
              {status === 'no_show' && (
                <div className="text-xs rounded-lg px-2.5 py-2 bg-orange-50 dark:bg-orange-900/10 border border-orange-400/60 dark:border-orange-600/50 flex items-start gap-2">
                  <span><Icon name="ban" /></span>
                  <div className="text-orange-700 dark:text-orange-400">
                    <div className="font-medium">
                      {t('cand_no_show_was')
                        .replace('{date}', candidate?.interview_date || '—')
                        .replace('{time}', candidate?.interview_time ? ' ' + candidate.interview_time.slice(0, 5) : '')}
                    </div>
                    <div className="opacity-80 mt-0.5">{t('cand_reschedule_hint')}</div>
                    {/* Clears the failed date/time so the Date field is ready for the NEW slot;
                        status stays no_show until a new date is picked (handleInterviewDate). */}
                    <button
                      onClick={() => { setInterviewDate(''); setInterviewTime('') }}
                      className="mt-2 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold hover:bg-orange-600 transition-colors">
                      {t('cand_reschedule_btn')}
                    </button>
                  </div>
                </div>
              )}

              {/* Interview details */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">{t('cand_date_label')}</label>
                  <DateInput value={interviewDate} onChange={handleInterviewDate} className={inp} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">{t('cand_time_label')}</label>
                  <TimeInput value={interviewTime} onChange={setInterviewTime} className={inp} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_interviewer_label') }</label>
                  <input value={interviewer} onChange={e => setInterviewer(e.target.value)}
                    {...{placeholder: t('cand_interviewer_placeholder')}} className={inp} />
                </div>
                <div className="col-span-2">
                  {/* B1301 — a saved candidate gets the THREAD; a new one keeps the textarea,
                      because there is no candidate id yet to hang a log row on and the text
                      still has to survive the first save. */}
                  {isEdit && candidate?.id
                    ? <CandidateNoteThread candidateId={candidate.id} legacyNotes={candidate.notes || ''} />
                    : <>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">{ t('cand_modal_notes_label') }</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                          {...{placeholder: t('cand_notes_placeholder')}}
                          rows={4} className={`${inp} resize-none`} />
                      </>}
                </div>
                {/* Clearing = cancelling a PLANNED interview → back to 'new'. Hidden for
                    no_show: that date already happened (it's history, not a plan) — the
                    banner's Reschedule button is the right tool there. 2026-07-11. */}
                {interviewDate && status !== 'no_show' && (
                  <div className="col-span-2">
                    <button onClick={() => { setInterviewDate(''); setInterviewTime(''); setInterviewer(''); setStatus('new') }}
                      className="text-xs text-red-500 hover:underline">
                      {t('cand_clear_interview')}
                    </button>
                  </div>
                )}
              </div>

              {/* Action buttons — after notes. STABLE order regardless of state:
                  Interviewed → No-show → Hire → Starting → Reject (user ask 2026-07-11). */}
              {!isHired && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {/* Once interviewed (saved), this is a state indicator — undo lives in the
                      footer with a 60s countdown, after which the fact is final. */}
                  <button onClick={() => {
                    if (!isEdit) toggleLocalStatus('interviewed')
                    else if (status !== 'interviewed' && confirm(t('cand_interviewed_confirm').replace('{name}', candidate.name))) handleQuickStatus('interviewed')
                  }} disabled={saving || (isEdit && status === 'interviewed')}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${status === 'interviewed' ? 'bg-amber-500 text-white border-amber-500' : 'border-border text-muted-foreground hover:border-amber-400 hover:text-amber-600'}`}>
                    {t('cand_interviewed_btn')}
                  </button>
                  {/* No-show — only from 'scheduled'; no undo (see banner above). */}
                  {isEdit && status === 'scheduled' && (
                    <button
                      onClick={() => {
                        if (confirm(t('cand_no_show_confirm').replace('{name}', candidate.name))) handleQuickStatus('no_show')
                      }}
                      disabled={saving}
                      className="px-3 py-1.5 rounded-lg border border-border text-muted-foreground text-sm font-medium hover:border-orange-400 hover:text-orange-600 transition-colors">
                      {t('cand_no_show_btn')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (!isEdit) handlePromote()
                      else if (confirm(t('cand_hire_confirm').replace('{name}', candidate.name))) handlePromote()
                    }}
                    disabled={promoting || saving}
                    className="px-3 py-1.5 rounded-lg border border-emerald-400 text-emerald-700 dark:text-emerald-400 text-sm font-medium hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors disabled:opacity-50">
                    {promoting ? t('cand_modal_creating') : t('cand_hire_btn')}
                  </button>
                  <button
                    onClick={() => {
                      if (status === 'starting') { setStartDate(''); setStartTime(''); handleStartingStatus('interviewed', '', '') }
                      else setShowStartPicker(v => !v)
                    }}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${status === 'starting' ? 'bg-cyan-500 text-white border-cyan-500' : 'border-border text-muted-foreground hover:border-cyan-400 hover:text-cyan-500'}`}>
                    {status === 'starting' ? t('cand_undo_starting') : t('cand_starting_btn')}
                  </button>
                  <button
                    onClick={() => {
                      if (!isEdit) toggleLocalStatus('rejected')
                      else if (status === 'rejected') handleQuickStatus('new')
                      else if (confirm(t('cand_reject_confirm').replace('{name}', candidate.name))) handleQuickStatus('rejected')
                    }}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${status === 'rejected' ? 'bg-red-500 text-white border-red-500' : 'border-border text-muted-foreground hover:border-red-400 hover:text-red-500'}`}>
                    {status === 'rejected' ? t('cand_undo_reject') : t('cand_reject_btn')}
                  </button>
                </div>
              )}

              {/* Start date picker — revealed by Starting button */}
              {!isHired && showStartPicker && status !== 'starting' && (
                <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-cyan-400/40 bg-cyan-50/50 dark:bg-cyan-900/10">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">{t('cand_start_date_label')}</label>
                    <DateInput value={startDate} onChange={setStartDate} className={inp} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Time</label>
                    <TimeInput value={startTime} onChange={setStartTime} className={inp} />
                  </div>
                  <div className="col-span-2">
                    <button
                      onClick={() => { if (startDate) { setShowStartPicker(false); handleStartingStatus('starting', startDate, startTime) } }}
                      disabled={!startDate || saving}
                      className="px-3 py-1.5 rounded-lg bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 transition-colors disabled:opacity-40">
                      {t('cand_start_confirm')}
                    </button>
                  </div>
                </div>
              )}

              {/* Full event history — scheduled / rescheduled / no-show / etc., same log as the card */}
              {/* key={status}: re-fetch the log after a quick status change in this modal */}
              {isEdit && <CandidateTimeline key={status} candidateId={candidate.id} variant="modal" />}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-border">
          {isEdit && !isHired && canDelete && (
            <button onClick={() => { if (confirm(t('cand_delete_confirm').replace('{name}', candidate.name))) { onDelete(candidate.id); onClose() } }}
              className="text-sm font-medium text-destructive hover:bg-destructive/10 px-3 py-2 rounded-lg transition-colors">
              {t('cand_delete')}
            </button>
          )}
          {/* Timed undo — same round button + countdown badge as AgentModal's hire revert */}
          {isEdit && status === 'interviewed' && undoSecs > 0 && (
            <button onClick={handleUndoInterviewed} disabled={saving}
              title={`${t('cand_undo_interviewed')} (${undoSecs}s)`}
              className="w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all shrink-0 relative border-amber-400 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              <span className="absolute -top-1.5 -right-1.5 text-[9px] bg-amber-400 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold tabular-nums">
                {undoSecs}s
              </span>
            </button>
          )}
          <div className="flex-1" />
          {/* no Cancel button — the top-right ✕ is the single close affordance (2026-07-11) */}
          {!isHired && (
            <button onClick={handleSave} disabled={saving}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60 transition-opacity">
              {saving ? t('cand_modal_saving') : isEdit ? t('cand_modal_save_changes') : t('cand_modal_add_btn')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main tab ─────────────────────────────────────────────────────────────────
export function CandidatesTab({ onAgentCreated, offices = [], config = {}, revertCandidateId, onRevertHandled, addSignal = 0, onAddHandled, openCandidateId = null, onOpenHandled, role, me = null }) {
  const { candidates, loading, error, create, update, remove, promoteToAgent } = useCandidates(offices, me)
  // Delete is destructive + candidates carry hiring history — admin only (UI gate)
  const canDelete = can(role, 'candidates.delete')
  const [modal,       setModal]       = useState(null)

  // Auto-open candidate after hire revert
  useEffect(() => {
    if (revertCandidateId && candidates.length > 0) {
      const c = candidates.find(x => x.id === revertCandidateId)
      if (c) { setModal({ candidate: c, initialTab: 'interview' }); onRevertHandled?.() }
    }
  }, [revertCandidateId, candidates])
  const [search,      setSearch]      = useState('')
  const [filterStatus, setFilterStatus] = useState('active')
  const [filterDist,  setFilterDist]  = useState([])
  const [filterLang,  setFilterLang]  = useState([])
  const [sort,        setSort]        = useState('status')
  // default = calendar (interviews) view; a user's own toggle choice still persists
  const [view,        setView]        = useViewPreference('candidates', 'interviews')

  // Add-candidate asked for from outside this tab (the Desk's "+ დამატება" menu, the phone's
  // header "+"). App is what routes it here — it is the only component that can change tabs —
  // and it arrives as a counter so that pressing + twice re-opens the form. onAddHandled
  // clears it, which is what stops a later visit to this tab from re-opening the modal.
  // ⚠️ This replaced a window-event listener that nothing had ever dispatched. Do not restore
  // it alongside this: two mechanisms for one modal is how the second one silently stops
  // being tested.
  useEffect(() => {
    if (addSignal > 0) { setModal({ candidate: null }); onAddHandled?.() }
  }, [addSignal])

  // One named candidate, opened from the Desk's new-applicant banner. initialTab 'interview'
  // because the banner's sentence is "call them to book a meeting" — the section that books
  // it is what the click should land on, not the top of a form.
  useEffect(() => {
    if (!openCandidateId || candidates.length === 0) return
    const c = candidates.find(x => x.id === openCandidateId)
    if (c) { setModal({ candidate: c, initialTab: 'interview' }); onOpenHandled?.() }
  }, [openCandidateId, candidates])

  // Hired candidates have moved to Agents — exclude from pipeline
  const pipeline = useMemo(() => candidates.filter(c => c.status !== 'hired'), [candidates])

  const filtered = useMemo(() => {
    let list = pipeline
    if (filterStatus === 'active') list = list.filter(c => c.status !== 'rejected')
    else if (filterStatus) list = list.filter(c => c.status === filterStatus)
    if (filterDist.length)   list = list.filter(c => filterDist.includes(c.district))
    if (filterLang.length)   list = list.filter(c => Array.isArray(c.languages) && c.languages.some(l => filterLang.includes(l)))
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.position || '').toLowerCase().includes(q) ||
        (c.email    || '').toLowerCase().includes(q)
      )
    }
    // Sort
    const today = todayStr()
    const _nowMins = nowHour() * 60 + tzMinute()
    const needsAction = c => (
      (c.status === 'scheduled' && c.interview_date && (
        c.interview_date < today ||
        (c.interview_date === today && c.interview_time &&
          _nowMins > Number(c.interview_time.slice(0,2)) * 60 + Number(c.interview_time.slice(3,5)) + 30)
      )) ||
      (c.status === 'starting' && c.start_date && c.start_date < today)
    )
    if (sort === 'status') {
      list = [...list].sort((a, b) => {
        if (a.status === 'rejected' && b.status !== 'rejected') return 1
        if (b.status === 'rejected' && a.status !== 'rejected') return -1
        if (needsAction(a) && !needsAction(b)) return -1
        if (needsAction(b) && !needsAction(a)) return 1
        const aHas = a.interview_date && a.interview_date >= today
        const bHas = b.interview_date && b.interview_date >= today
        if (aHas && bHas) return (a.interview_date + (a.interview_time || '')).localeCompare(b.interview_date + (b.interview_time || ''))
        if (aHas) return -1
        if (bHas) return 1
        // no upcoming interview on either side: recently added first (was alphabetical,
        // which buried a just-added candidate mid-list)
        return (b.created_at || '').localeCompare(a.created_at || '') || a.name.localeCompare(b.name)
      })
    } else if (sort === 'added') {
      // by created_at, newest first — ids are random UUIDs, comparing them was no order at all
      list = [...list].sort((a, b) =>
        (b.created_at || '').localeCompare(a.created_at || '') || (a.id > b.id ? 1 : -1))
    } else if (sort === 'interview') {
      list = [...list].sort((a, b) => {
        const aDate = a.interview_date ? a.interview_date + (a.interview_time || '') : ''
        const bDate = b.interview_date ? b.interview_date + (b.interview_time || '') : ''
        if (!aDate && !bDate) return 0
        if (!aDate) return 1
        if (!bDate) return -1
        return bDate.localeCompare(aDate)
      })
    } else if (sort === 'name') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name))
    }
    return list
  }, [pipeline, filterStatus, filterDist, filterLang, search, sort])

  const activeFilters = [filterStatus && filterStatus !== 'active'].filter(Boolean).length + filterDist.length + filterLang.length

  // Briefly highlight the saved candidate's row/card so the eye lands on it after the
  // modal closes (it may have moved to a different section by the new sort rules)
  const [flashId, setFlashId] = useState(null)
  async function handleSave(data) {
    const c = modal?.candidate ? await update(modal.candidate.id, data) : await create(data)
    setFlashId(c.id)
    setTimeout(() => setFlashId(f => (f === c.id ? null : f)), 3000)
    return c
  }

  async function handleHire(candidateId) {
    const preHireStatus = candidates.find(c => c.id === candidateId)?.status || 'new'
    const agent = await promoteToAgent(candidateId)
    if (agent) {
      const now = String(Date.now())
      try {
        localStorage.setItem(`hire_ts_${candidateId}`, now)
        localStorage.setItem(`hire_ts_agent_${agent.id}`, JSON.stringify({ ts: now, candidateId, preHireStatus }))
      } catch {}
      setModal(null)
    }
    onAgentCreated?.(agent ? { ...agent, _preHireStatus: preHireStatus } : agent)
  }

  function openEdit(candidate, initialTab) {
    setModal({ candidate, initialTab })
  }

  if (loading) return (
    <div className="flex items-center justify-center py-24 text-muted-foreground text-sm gap-2">
      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
      </svg>
      Loading…
    </div>
  )

  if (error) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3 text-sm">
      <span className="text-3xl"><Icon name="alert-triangle" size={30} /></span>
      <p className="text-destructive font-medium">Failed to load candidates</p>
      <p className="text-muted-foreground text-xs">{error}</p>
      <button onClick={() => window.location.reload()}
        className={pill('none', { door: true, extra: 'border border-border font-medium hover:bg-accent transition-colors' })}>
        Retry
      </button>
    </div>
  )

  const districts = config.districts || []
  const languages = config.languages || []

  return (
    <div className="space-y-3">
      {/* Filter bar — row 1: view toggle + search; row 2: filter pills */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {/* Candidates has only two views: calendar (📅, default) + cards. List view was
              removed 2026-07-11 (user ask) — hence a local toggle, not the shared ViewToggle. */}
          <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
            <button onClick={() => setView('interviews')} title={t('cand_view_interviews')}
              className={`px-2.5 py-1.5 rounded-md text-sm transition-all ${view === 'interviews' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon name="calendar" size={14} />
            </button>
            <button onClick={() => setView('card')} title={t('agents_view_card')}
              className={`px-2.5 py-1.5 rounded-md text-sm transition-all ${view !== 'interviews' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              ⊞
            </button>
          </div>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"><Icon name="search" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              {...{placeholder: t('cand_search_placeholder')}}
              className="w-full rounded-full border border-input pl-8 pr-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <button onClick={() => setModal({ candidate: null })}
            className="flex items-center gap-1 shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap">
            {/* mobile: icon-only "+" to leave room for the search bar */}
            <span className="sm:hidden text-base leading-none">+</span>
            <span className="hidden sm:inline">{t('cand_add')}</span>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <SingleSelect
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: '', label: t('agents_filter_all') },
              { value: 'active', label: t('cand_active') },
              ...Object.entries(CANDIDATE_STATUSES).filter(([k]) => k !== 'hired').map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />

          <MultiSelect label={t('cand_modal_district_label')} options={districts} selected={filterDist} onChange={setFilterDist} />

          <MultiSelect label={t('agents_filter_language')} options={languages} selected={filterLang} onChange={setFilterLang} />

          <SingleSelect
            value={sort}
            onChange={setSort}
            options={[
              { value: 'status', label: t('cand_sort_status') },
              { value: 'added', label: t('cand_sort_added') },
              { value: 'interview', label: t('cand_sort_interview') },
              { value: 'name', label: t('cand_sort_name') },
            ]}
          />
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        {filtered.length} {filtered.length === 1 ? t('cand_count_one') : t('cand_count_many')}
        {(activeFilters > 0 || search) && ` ${t('cand_count_of')} ${pipeline.length}`}
      </div>

      {/* List */}
      {view === 'interviews' ? (
        <InterviewsView candidates={filtered} onEdit={openEdit} flashId={flashId}
          onUpdate={update} onPromote={handleHire} onDelete={remove} canDelete={canDelete} />
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-sm gap-3">
          <span className="text-4xl"><Icon name="search" size={36} /></span>
          <p>{activeFilters > 0 || search ? t('cand_no_match_filter') : t('cand_no_candidates')}</p>
          {(activeFilters > 0 || search) && (
            <button onClick={() => { setFilterStatus('active'); setFilterDist([]); setFilterLang([]); setSearch('') }}
              className={pill('none', { door: true, extra: 'border border-border font-medium hover:bg-accent transition-colors' })}>
              {t('filter_clear_all')}
            </button>
          )}
        </div>
      ) : (
        /* any non-calendar view (incl. a stale stored 'list' preference) → card grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(c => (
            <CandidateCard key={c.id} candidate={c} canDelete={canDelete} flash={c.id === flashId}
              onEdit={openEdit} onUpdate={update} onPromote={handleHire} onDelete={remove} />
          ))}
        </div>
      )}

      {modal !== null && (
        <CandidateModal
          candidate={modal.candidate}
          initialTab={modal.initialTab}
          config={config}
          me={me}
          offices={offices}
          canDelete={canDelete}
          // Who BLOCKS an attack is not necessarily who POSTED the link, so management sees every
          // link; a plain recruiter sees their own. The accept-only-ID-links switch closes a door
          // for the whole company and stays with the admin.
          seeAllVacancyLinks={role === 'admin' || role === 'general_manager'}
          canSetVacancyLinkPolicy={role === 'admin'}
          onSave={handleSave}
          onDelete={remove}
          onPromote={handleHire}
          onClose={(agent) => { setModal(null); if (agent?.id) onAgentCreated?.(agent) }}
        />
      )}
    </div>
  )
}
